import { format, isValid, parseISO } from "date-fns";
import React, { useRef, useState } from "react";
import { usePagination, useTable } from "react-table";
import styled from "styled-components";
import { getBaseURL } from "../../config/runtime";
import { UsersData } from "../../domain/user/users_data";
import { UserData } from "../../domain/user/user_data";
import { isErrorAuth } from "../../error/error";
import { fireUserSignInEvent } from "../../event/user_sign_in_event";
import { fireUserSignOutEvent } from "../../event/user_sign_out_event";
import { resolveResp, getResolveJSON } from "../../util/fetch";
import { DateRangePicker } from "../date/date_range_picker";
import { Search } from "../form/input/search";
import { MessageYesNo } from "../message/message_yes_no";
import Popup from "../popup/Popup";
import Spinner from "../Spinner";
import { Pagination } from "./Pagination";

export interface Props {
  onUserSelected: (user: UserData) => void;
}

const Styles = styled.div`
  background: #fff;
  margin-right: 2rem;

  table {
    border-spacing: 0;
    font-size: 0.9rem;
    margin-bottom: 1rem;

    thead {
      th {
        font-weight: bold;
        text-align: left;
      }
    }

    tbody {
      tr {
        :nth-child(even) {
          background: #eee;
        }
        &:hover {
          box-shadow: 0 0 0.2rem #bbb;
          cursor: pointer;
          position: relative;
        }
      }
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem 0.6rem;

      :last-child {
      }
    }
  }
`;

const FiltersPanel = styled.section`
	background: #efefef;
  border-radius: .25rem;
  display: flex;
  align-items: start;
  margin-bottom: 1.25rem;
  padding: 1rem;
  justify-content: center;
`;

const Button = styled.button`
  background-color: white;
  border-width: 1px;
  border-color: #777;
  border-radius: .3rem;
  margin-right: 1rem;
  padding: .2rem;
  width: 5rem;
  font-weight: bold;
  :hover {
    cursor: pointer;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 3rem;
  margin: auto;
`;

const Tr = styled.tr`
  td {
    :first-child {
      border-left: ${(props: any) => (props.selected ? ".25rem solid pink" : 0)};
    }
  }
`;

  function createFilterQueryParams(search: string, fromDate: Date|null, toDate: Date|null) {
    let queryParams = search ? `&search=${search}` : '';
    queryParams += isValid(fromDate) ? `&filters[from]=${format(fromDate, 'yyyy-MM-dd')}` : '';
    queryParams += isValid(toDate) ? `&filters[to]=${format(toDate, 'yyyy-MM-dd')}` : '';
    return queryParams;
  }

// Let's add a fetchData method to our Table component that will be used to fetch
// new data when pagination state changes
// We can also add a loading state to let our table know it's loading new data
function Table({
  columns,
  data,
  fetchData,
  loading,
  setLoading,
  pageCount: controlledPageCount,
  onRowSelected,
}) {
  
  const anchorRef = useRef();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState<Date|null>(null);
  const [toDate, setToDate] = useState<Date|null>(null);
  const [originalPageIndex, setOriginalPageIndex] = useState(0);

  /**
  *
  * https://stackoverflow.com/questions/34586671/download-pdf-file-using-jquery-ajax
  *
  * @param string url
  * @param string return type JSON or CSV.
  */
  const downloadUsers = async (returnType: 'json' | 'csv') => {
    try {
      const returnTypeQueryParam = returnType === 'json' ? 'JSON_DOWNLOAD' : 'CSV_DOWNLOAD';
      const filterParams = createFilterQueryParams(searchTerm, fromDate, toDate);
      const url = `${getBaseURL()}/v1/client/users?page_size=-1${filterParams}&return_type=${returnTypeQueryParam}`;

      setLoading(true);
      const timestamp = format(new Date(), 'yyyy-MM-dd');
      const response = await resolveResp({ method: 'GET', url });
      const blobby = await response.blob();
      const downloadUrl = URL.createObjectURL(blobby);
      anchorRef.current.href = downloadUrl;
      anchorRef.current.download = `user_data_${timestamp}.${returnType}`;
      anchorRef.current.click();
      setLoading(false);
    } catch (e) {
      // TODO: Handle error.
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    // Get the state from the instance
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 }, // Pass our hoisted table state
      manualPagination: true,
      // Tell the usePagination
      // hook that we'll handle our own data fetching
      // This means we'll also have to provide our own
      // pageCount.
      pageCount: controlledPageCount,
    },
    usePagination
  );

  // Listen for changes in pagination and use the state to fetch our new data
  React.useEffect(() => {
    fetchData({ pageIndex, pageSize, searchTerm, fromDate, toDate });
  }, [fetchData, pageIndex, pageSize, fromDate, toDate]);

  const clearSelection = () => {
    setSelectedRow(null);
    onRowSelected(null);
  };
  const onSearch = (term: string, signal: AbortSignal) => {
    clearSelection();
    setSearchTerm(term);
    setOriginalPageIndex(pageIndex);
    gotoPage(0);
    fetchData({ pageSize, pageIndex: 0, searchTerm: term, fromDate, toDate });
  };

  const onSearchClear = () => {
    setSearchTerm("");
    gotoPage(originalPageIndex);
    fetchData({ pageSize, originalPageIndex, searchTerm: "" });
  };

  const [selectedRow, setSelectedRow] = React.useState<number | null>(null);

  const pageNum = pageIndex + 1;
  const startIndex = pageNum * pageSize - pageSize + 1;
  const endIndex = pageNum * pageSize;
  return (
    <>
      <section>
        <label style={{ display: 'block', marginBottom: '.5rem'}}>Download user data</label>
        <section style={{ marginTop: '0', marginBottom: '2rem', display: data.length > 0 ? 'flex' : 'none' }}>
          <Button disabled={loading} onClick={() => downloadUsers('json')}>JSON</Button>
          <Button disabled={loading} onClick={() => downloadUsers('csv')}>CSV</Button>
          <a ref={anchorRef} />
          <div style={{ display: loading ? 'inline-block' : 'none', width: '1.5rem', height: '1.5rem' }}>
            <Spinner />
          </div>
        </section>
      </section>

    <FiltersPanel>

      <Search
        panelStyle={{ fill: "#555" }}
        onSearch={onSearch}
        onClear={onSearchClear}
      />

      <DateRangePicker
        onFromChange={(from) => setFromDate(from)}
        onToChange={(to) => setToDate(to)}
      />

    </FiltersPanel>

      <table {...getTableProps()}>
        <thead className="table__column-header">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render("Header")}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                onClick={() => {
                  setSelectedRow(row.original.id);
                  onRowSelected(row.original);
                }}
                className={row.original.id === selectedRow ? "table__row--selected" : ""}
              >
                {row.cells.map((cell) => {
                  return <td {...cell.getCellProps()}>{cell.render("Cell")}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <Pagination
        startIndex={startIndex}
        endIndex={endIndex}
        total={pageOptions.length}
        pageSize={pageSize}
        setPageSize={setPageSize}
        hasPrevious={canPreviousPage}
        hasNext={canNextPage}
        previousPage={() => previousPage()}
        nextPage={() => {
          nextPage();
        }}
      ></Pagination>
    </>
  );
}

export const UserTable = (props: Props) => {
  const columns = React.useMemo(
    () => [
      {
        Header: "Id",
        accessor: "id",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "First Name",
        // accessor: "firstName",
        accessor: "name_first",
      },
      {
        Header: "Last Name",
        // accessor: "lastName",
        accessor: "name_last",
      },
      {
        Header: "Created On",
        Cell: ({ row }) => (
          <label>{format(parseISO(row.original.created_on), "dd MMM yyyy")}</label>
        ),
      },
    ],
    []
  );

  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [pageCount, setPageCount] = React.useState(0);
  const [showError, setShowError] = React.useState(false);

  const fetchIdRef = React.useRef(0);

  const fetchData = React.useCallback(async ({ pageSize, pageIndex, searchTerm, fromDate, toDate }) => {
    // Give this fetch an ID
    const fetchId = ++fetchIdRef.current;

    setLoading(true);

    const url = `/v1/client/users?page_num=${pageIndex}&page_size=${pageSize}`
      + createFilterQueryParams(searchTerm, fromDate, toDate);
    
    const resp = await getResolveJSON<UsersData | { object: "error_auth" }>(getBaseURL() + url);
    if (isErrorAuth(resp)) {
      setShowError(true);
      window.rendivo.addPostUserLoginAction(() => {
        fetchData({ pageSize, pageIndex, searchTerm, fromDate, toDate});
      });
    } else if (
      // Only update the data if this is the latest fetch
      fetchId === fetchIdRef.current
    ) {
      setData(resp.data);
      setPageCount(resp.total);
      setLoading(false);
    }
  }, []);

  return (
    <>
      {showError ? (
        <Popup
          onClose={() => {
            setShowError(false);
          }}
          title="Session Expired"
        >
          <MessageYesNo
            text="Your session has expired, would you like to re-login to continue?"
            onYes={() => {
              setShowError(false);
              fireUserSignInEvent("SESSION_EXPIRED");
            }}
            onNo={() => {
              setShowError(false);
              fireUserSignOutEvent();
            }}
          />
        </Popup>
      ) : null}
      <Styles>
        <Table
          columns={columns}
          data={data}
          fetchData={fetchData}
          loading={loading}
          setLoading={setLoading}
          pageCount={pageCount}
          onRowSelected={props.onUserSelected}
        />
      </Styles>
    </>
  );
};
